import React, { memo, useCallback, useEffect, useState } from 'react';
import { FlatList, Image, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import type { TonyteActivity } from '../types';
import { parseActivityDate } from '../utils/activityDates';
import { useTheme } from '../theme/useTheme';
import { Radius, Spacing, Typography } from '../theme';
import { useI18n } from '../i18n';
import { getWebFallbackImage, resolveActivityImage } from '../utils/activityImages';

type Props = {
  events: TonyteActivity[];
  selectedDate: Date;
  onPress: (activity: TonyteActivity) => void;
  testID?: string;
};

const LOCALES: Record<string, string> = { de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' };

const ContinuedCard = memo(({ activity, locale, onPress }: {
  activity: TonyteActivity;
  locale: string;
  onPress: (activity: TonyteActivity) => void;
}) => {
  const { theme } = useTheme();
  const [imageSource, setImageSource] = useState<number | string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const parsed = parseActivityDate(activity);
  const startLabel = parsed.startAt?.toLocaleDateString(locale, { month: 'short', day: 'numeric' }) ?? activity.startDate;
  const endLabel = activity.endTime?.trim() || parsed.endAt?.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) || '';
  useEffect(() => {
    let active = true;
    setImageSource(null);
    setImageFailed(false);
    setUsedFallback(false);
    resolveActivityImage(activity)
      .then(result => { if (active) setImageSource(result.source); })
      .catch(() => { if (active) setImageFailed(true); });
    return () => { active = false; };
  }, [activity]);
  return (
    <TouchableOpacity
      activeOpacity={0.86}
      onPress={() => onPress(activity)}
      style={[styles.card, { backgroundColor: theme.colors.surfaceElevated, borderColor: theme.colors.borderStrong }]}
      accessibilityRole="button"
      accessibilityLabel={activity.title}
    >
      <LinearGradient
        pointerEvents="none"
        colors={[theme.colors.accentSecondary, theme.colors.accent]}
        style={styles.accentRail}
      />
      <View style={[styles.thumbnail, { backgroundColor: theme.colors.accentSecondarySoft, borderColor: theme.colors.accentSecondaryBorder }]}> 
        {imageSource && !imageFailed ? (
          <Image
            source={typeof imageSource === 'number' ? imageSource : { uri: imageSource }}
            resizeMode="cover"
            resizeMethod="resize"
            style={styles.thumbnailImage}
            onError={() => {
              if (!usedFallback) {
                setUsedFallback(true);
                setImageSource(getWebFallbackImage(activity));
              } else {
                setImageFailed(true);
              }
            }}
          />
        ) : (
          <Icon name="calendar" size={16} color={theme.colors.accentSecondary} />
        )}
        <View style={[styles.continuedBadge, { backgroundColor: theme.colors.navBackground }]}> 
          <Icon name="corner-down-right" size={9} color={theme.colors.accentSecondary} />
        </View>
      </View>
      <View style={styles.copy}>
        <Text style={[styles.title, { color: theme.colors.textPrimary }]} numberOfLines={1}>{activity.title}</Text>
        <View style={styles.metaRow}>
          <Text style={[styles.meta, { color: theme.colors.textMuted }]} numberOfLines={1}>Started {startLabel}</Text>
          <View style={[styles.dot, { backgroundColor: theme.colors.accent }]} />
          <Text style={[styles.until, { color: theme.colors.accent }]} numberOfLines={1}>{endLabel ? `Until ${endLabel}` : 'Continues today'}</Text>
        </View>
        {(activity.venueName || activity.locationName) ? (
          <View style={styles.placeRow}>
            <Icon name="map-pin" size={10} color={theme.colors.iconMuted} />
            <Text style={[styles.place, { color: theme.colors.textSecondary }]} numberOfLines={1}>{activity.venueName || activity.locationName}</Text>
          </View>
        ) : null}
      </View>
      <Icon name="chevron-right" size={15} color={theme.colors.iconMuted} />
    </TouchableOpacity>
  );
});

const ContinuedEventsCarousel: React.FC<Props> = ({ events, selectedDate, onPress, testID }) => {
  const { t, language } = useI18n();
  const { theme } = useTheme();
  const locale = LOCALES[language] ?? 'en-US';
  const renderItem = useCallback(({ item }: { item: TonyteActivity }) => (
    <ContinuedCard activity={item} locale={locale} onPress={onPress} />
  ), [locale, onPress]);
  if (!events.length) return null;
  return (
    <View style={[styles.section, { borderColor: theme.colors.border, backgroundColor: theme.colors.surfaceGlass }]} testID={testID}>
      <View style={styles.headingRow}>
        <LinearGradient
          colors={[theme.colors.accentSecondary, theme.colors.accent]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.headingIcon}
        >
          <Icon name="star" size={13} color="#FFFFFF" />
          <View style={styles.emblemSpark} />
        </LinearGradient>
        <View style={styles.headingCopy}>
          <Text style={[styles.heading, { color: theme.colors.textPrimary }]}>{t('feed.vibeContinues', 'The Vibe Continues')}</Text>
          <Text style={[styles.subtitle, { color: theme.colors.textMuted }]} numberOfLines={1}>
            {t('feed.vibeContinuesSubtitle', 'Started earlier. Worth catching now.')}
          </Text>
        </View>
        <Text style={[styles.count, { color: theme.colors.accentSecondary }]}>{events.length}</Text>
      </View>
      <FlatList
        horizontal
        nestedScrollEnabled
        data={events}
        renderItem={renderItem}
        keyExtractor={item => item.id}
        ItemSeparatorComponent={Separator}
        contentContainerStyle={styles.list}
        showsHorizontalScrollIndicator={false}
        decelerationRate="fast"
        snapToInterval={266}
        initialNumToRender={4}
        windowSize={5}
        extraData={selectedDate.getTime()}
      />
    </View>
  );
};

const Separator = () => <View style={styles.separator} />;

const styles = StyleSheet.create({
  section: {
    marginTop: Spacing.md,
    marginBottom: Spacing.base,
    marginHorizontal: Platform.OS === 'web' ? 0 : -4,
    paddingVertical: 12,
    borderWidth: 1,
    borderRadius: Radius.lg,
    overflow: 'hidden',
  },
  headingRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, marginBottom: 9 },
  headingIcon: { width: 30, height: 30, borderRadius: 10, alignItems: 'center', justifyContent: 'center', marginRight: 9 },
  emblemSpark: { position: 'absolute', top: 5, right: 5, width: 3, height: 3, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.88)' },
  headingCopy: { flex: 1, minWidth: 0 },
  heading: { fontSize: 14, lineHeight: 18, fontWeight: Typography.extrabold },
  subtitle: { marginTop: 1, fontSize: 10, lineHeight: 13, fontWeight: Typography.medium },
  count: { fontSize: 11, fontWeight: Typography.extrabold },
  list: { paddingHorizontal: 12, paddingRight: 18 },
  separator: { width: 10 },
  card: {
    width: 256,
    minHeight: 86,
    borderWidth: 1,
    borderRadius: Radius.md,
    paddingVertical: 11,
    paddingLeft: 15,
    paddingRight: 10,
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
  },
  accentRail: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 3 },
  thumbnail: { width: 58, height: 62, borderRadius: 11, borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginRight: 10, overflow: 'hidden' },
  thumbnailImage: { width: '100%', height: '100%' },
  continuedBadge: { position: 'absolute', right: 4, bottom: 4, width: 18, height: 18, borderRadius: 7, alignItems: 'center', justifyContent: 'center' },
  copy: { flex: 1, minWidth: 0 },
  title: { fontSize: 13, lineHeight: 17, fontWeight: Typography.bold },
  metaRow: { flexDirection: 'row', alignItems: 'center', marginTop: 3, minWidth: 0 },
  meta: { fontSize: 9.5, lineHeight: 12, fontWeight: Typography.medium },
  dot: { width: 3, height: 3, borderRadius: 2, marginHorizontal: 6 },
  until: { fontSize: 9.5, lineHeight: 12, fontWeight: Typography.bold },
  placeRow: { flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 4 },
  place: { flex: 1, fontSize: 9.5, lineHeight: 12, fontWeight: Typography.medium },
});

export default memo(ContinuedEventsCarousel);
